import React, { useState, useEffect } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import CustomNavbar from "../CustomPermissions/CustomNavbar";
import "./sop.css";
import { getApiUrl, API_ENDPOINTS, API_BASE_URL } from "../../config/api";

const SopManagementPage = () => {
    const [sops, setSops] = useState([]);
    const [filteredSops, setFilteredSops] = useState([]);
    const [canEdit, setCanEdit] = useState(false);
    const [newSop, setNewSop] = useState({
        designation: "",
        docs: null,
        desc: "",
    });
    const [editingSop, setEditingSop] = useState(null);
    const [filter, setFilter] = useState("");
    const [designations, setDesignations] = useState([]);
    const [showCreateEditModal, setShowCreateEditModal] = useState(false);
    const [showViewDescModal, setShowViewDescModal] = useState(false);
    const [currentDesc, setCurrentDesc] = useState("");
    const [users, setUsers] = useState([]);

    useEffect(() => {
        fetchSops();
        fetchDesignations();
        fetchUsers();
    }, []);

    const fetchSops = async () => {
        try {
            // Use the new MANAGEMENT endpoint
            const response = await axios.get(`${API_BASE_URL}/SOP/sops/management/`, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem("access_token")}`,
                },
            });

            let sopData = response.data;
            if (response.data && response.data.data) {
                sopData = response.data.data;
                setCanEdit(response.data.can_edit === true);
            } else if (Array.isArray(response.data)) {
                sopData = response.data;
                setCanEdit(true); // Fallback for management view
            }

            setSops(sopData);
            setFilteredSops(sopData);
        } catch (error) {
            //   console.error("Error fetching SOPs:", error);
            setCanEdit(false);
            setSops([]);
            setFilteredSops([]);
            if (error.response && error.response.status === 403) {
                alert("You do not have permission to access SOP Management.");
            }
        }
    };

    const fetchDesignations = async () => {
        try {
            const response = await axios.get(`${API_BASE_URL}/designations/`, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem("access_token")}`,
                },
            });
            setDesignations(response.data);
        } catch (error) {
            // console.error("Error fetching designations:", error);
        }
    };

    const fetchUsers = async () => {
        try {
            const accessToken = localStorage.getItem("access_token");
            if (accessToken) {
                const response = await axios.get(
                    getApiUrl('/Authentication/manage/users/all/'),
                    {
                        headers: {
                            Authorization: `Bearer ${accessToken}`,
                        },
                    }
                );
                setUsers(response.data.users || []);
            }
        } catch (error) {
            console.error("Error fetching users:", error);
        }
    };

    const deleteSopFile = async (fileId) => {
        if (!window.confirm("Are you sure you want to delete this file?")) return;
        try {
            await axios.delete(`${API_BASE_URL}/SOP/files/delete/${fileId}/`, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem("access_token")}`,
                },
            });
            // Update the editingSop state to remove the file
            setEditingSop(prev => ({
                ...prev,
                files: prev.files.filter(f => f.id !== fileId)
            }));
            fetchSops(); // Refresh the list to be sure
        } catch (error) {
            alert("Error deleting file.");
        }
    };

    const handleFileChange = (e, type) => {
        const files = Array.from(e.target.files);
        if (editingSop) {
            setEditingSop({ ...editingSop, [`${type}_files`]: files });
        } else {
            setNewSop({ ...newSop, [`${type}_files`]: files });
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const designationValue = editingSop ? editingSop.designation : newSop.designation;
        const descValue = editingSop ? editingSop.desc : newSop.desc;
        const usersValue = editingSop ? editingSop.users : newSop.users;
        const nameValue = editingSop ? editingSop.name : newSop.name;

        if (!descValue || descValue.trim() === '' || descValue.toLowerCase() === 'undefined' || descValue === '<p>&nbsp;</p>' || descValue === '<p></p>') {
            alert('Please enter a description before submitting.');
            return;
        }

        const formData = new FormData();
        if (designationValue) formData.append("designation", designationValue);
        if (nameValue) formData.append("name", nameValue);
        formData.append("desc", descValue);

        if (usersValue && usersValue.length > 0) {
            usersValue.forEach(userId => {
                formData.append("users", userId);
            });
        }

        // Handle file uploads
        const pptFiles = editingSop ? editingSop.ppt_files : newSop.ppt_files;
        const pdfFiles = editingSop ? editingSop.pdf_files : newSop.pdf_files;
        const docFiles = editingSop ? editingSop.doc_files : newSop.doc_files;

        if (pptFiles) pptFiles.forEach(file => formData.append("ppt_files", file));
        if (pdfFiles) pdfFiles.forEach(file => formData.append("pdf_files", file));
        if (docFiles) docFiles.forEach(file => formData.append("doc_files", file));

        try {
            if (editingSop) {
                await axios.put(
                    `${API_BASE_URL}/SOP/sops/update/${editingSop.id}/`,
                    formData,
                    {
                        headers: {
                            "Content-Type": "multipart/form-data",
                            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
                        },
                    }
                );
                alert('SOP updated successfully!');
            } else {
                await axios.post(
                    `${API_BASE_URL}/SOP/sops/create/`,
                    formData,
                    {
                        headers: {
                            "Content-Type": "multipart/form-data",
                            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
                        },
                    }
                );
                alert('SOP created successfully!');
            }
            fetchSops();
            closeCreateEditModal();
        } catch (error) {
            console.error("Error saving SOP:", error);
            alert(`Error: ${error.response?.data?.error || 'An error occurred.'}`);
        }
    };

    const openCreateEditModal = (sop = null) => {
        if (sop) {
            setEditingSop({ 
                ...sop, 
                users: sop.users || [],
                desc: sop.descs__desc || "",
                name: sop.name || sop.descs__name || ""
            });
        } else {
            setEditingSop(null);
            setNewSop({ designation: "", name: "", desc: "", users: [], ppt_files: [], pdf_files: [], doc_files: [] });
        }
        setShowCreateEditModal(true);
    };

    const closeCreateEditModal = () => {
        setShowCreateEditModal(false);
        setEditingSop(null);
        setNewSop({ designation: "", name: "", desc: "", users: [], ppt_files: [], pdf_files: [], doc_files: [] });
    };

    const handleViewDescription = (desc) => {
        setCurrentDesc(desc);
        setShowViewDescModal(true);
    };

    const closeViewDescModal = () => {
        setShowViewDescModal(false);
        setCurrentDesc("");
    };

    const deleteSop = async (id) => {
        if (!window.confirm("Are you sure you want to delete this SOP?")) return;
        try {
            await axios.delete(`${API_BASE_URL}/SOP/sops/delete/${id}/`, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem("access_token")}`,
                },
            });
            fetchSops();
        } catch (error) {
            alert("Error deleting SOP.");
        }
    };

    return (
        <>
            <div className="d-flex flex-column flex-md-row">
                <CustomNavbar />
                <div className="container-fluid" style={{ margin: "20px auto", padding: "0 15px" }}>
                    <div className="d-flex flex-column flex-md-row justify-content-between align-items-start">
                        <h2 className="mb-4 text-center text-blue-500">SOP Management (Admin View)</h2>

                        <div className="d-flex flex-column flex-md-row align-items-center">
                            {/* Back Link */}
                            <Link
                                to="/soppage"
                                className="text-white bg-gray-600 py-2 px-4 rounded hover:bg-gray-500 mr-2"
                            >
                                Back to My SOPs
                            </Link>

                            <Link
                                to="/departmentpage"
                                className="text-white bg-blue-800 py-2 px-4 rounded hover:bg-blue-500 mr-2"
                            >
                                Add Department
                            </Link>

                            <Link
                                to="/designationpage"
                                className="text-white bg-blue-800  py-2 px-4 rounded hover:bg-blue-500"
                            >
                                Add Designation
                            </Link>

                            <Link
                                to="/assign-sops"
                                className="text-white bg-indigo-600 py-2 px-4 rounded hover:bg-indigo-500 mr-2"
                            >
                                Assign Users
                            </Link>

                            {canEdit && (
                                <button
                                    className="bg-blue-500 py-2 px-4 rounded ml-2 text-white hover:bg-blue-800 mb-4 mb-md-0"
                                    onClick={() => openCreateEditModal()}
                                >
                                    Add New SOP
                                </button>
                            )}
                        </div>
                    </div>

                    <div className="overflow-x-auto mx-auto w-full">
                        <table className="text-sm border-separate border-spacing-y-2 bg-white border-gray-300 " style={{ width: "100%", minWidth: "90%" }}>
                            <thead className="table-dark">
                                <tr className="bg-blue-500">
                                    <th className="py-2 px-4 text-center">#</th>
                                    <th className="py-2 px-4 text-center">Designation</th>
                                    <th className="py-2 px- text-center">Department</th>
                                    <th className="py-2 px-4 text-center">SOP Title</th>
                                    <th className="py-2 px-4 text-center">Assigned Users</th>
                                    <th className="py-2 px-4 text-center">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredSops.map((sop, index) => (
                                    <tr key={sop.id}>
                                        <td className="py-2 px-4 border-b text-center">{index + 1}</td>
                                        <td className="py-2 px-4 border-b text-center">{sop.designation__name || "N/A"}</td>
                                        <td className="py-2 px-4 border-b text-center">{sop.designation__dept__name || "N/A"}</td>
                                        <td className="py-2 px-4 border-b text-center">{sop.name || sop.descs__name}</td>
                                        <td className="py-2 px-4 border-b text-center">
                                            {sop.users && sop.users.length > 0 ? (
                                                <div title={sop.users_names ? sop.users_names.join(", ") : ""}>
                                                    {sop.users.length} Users
                                                    {sop.users_names && sop.users_names.length > 0 && (
                                                        <span className="text-xs text-gray-500 block">
                                                            ({sop.users_names.slice(0, 2).join(", ")}{sop.users_names.length > 2 ? "..." : ""})
                                                        </span>
                                                    )}
                                                </div>
                                            ) : "None"}
                                        </td>

                                        <td className="py-2 px-4 border-b text-center">
                                            <button className="bg-blue-500 py-2 px-3 text-white rounded mr-2" onClick={() => handleViewDescription(sop.descs__desc)}>
                                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-eye" viewBox="0 0 16 16">
                                                    <path d="M16 8s-3-5.5-8-5.5S0 8 0 8s3 5.5 8 5.5S16 8 16 8M1.173 8a13 13 0 0 1 1.66-2.043C4.12 4.668 5.88 3.5 8 3.5s3.879 1.168 5.168 2.457A13 13 0 0 1 14.828 8q-.086.13-.195.288c-.335.48-.83 1.12-1.465 1.755C11.879 11.332 10.119 12.5 8 12.5s-3.879-1.168-5.168-2.457A13 13 0 0 1 1.172 8z" />
                                                    <path d="M8 5.5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5M4.5 8a3.5 3.5 0 1 1 7 0 3.5 3.5 0 0 1-7 0" />
                                                </svg>
                                            </button>
                                            {canEdit && (
                                                <>
                                                    <button className="bg-yellow-300 py-2 px-3 text-white rounded mr-2" onClick={() => openCreateEditModal(sop)}>
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-pen-fill" viewBox="0 0 16 16">
                                                            <path d="m13.498.795.149-.149a1.207 1.207 0 1 1 1.707 1.708l-.149.148a1.5 1.5 0 0 1-.059 2.059L4.854 14.854a.5.5 0 0 1-.233.131l-4 1a.5.5 0 0 1-.606-.606l1-4a.5.5 0 0 1 .131-.232l9.642-9.642a.5.5 0 0 0-.642.056L6.854 4.854a.5.5 0 1 1-.708-.708L9.44.854A1.5 1.5 0 0 1 11.5.796a1.5 1.5 0 0 1 1.998-.001" />
                                                        </svg>
                                                    </button>
                                                    <button className="bg-red-500 py-2 px-3 text-white rounded mr-2" onClick={() => deleteSop(sop.id)}>
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-trash-fill" viewBox="0 0 16 16">
                                                            <path d="M2.5 1a1 1 0 0 0-1 1v1a1 1 0 0 0 1 1H3v9a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2V4h.5a1 1 0 0 0 1-1V2a1 1 0 0 0-1-1H10a1 1 0 0 0-1-1H7a1 1 0 0 0-1 1zm3 4a.5.5 0 0 1 .5.5v7a.5.5 0 0 1-1 0v-7a.5.5 0 0 1 .5-.5M8 5a.5.5 0 0 1 .5.5v7a.5.5 0 0 1-1 0v-7A.5.5 0 0 1 8 5m3 .5v7a.5.5 0 0 1-1 0v-7a.5.5 0 0 1 1 0" />
                                                        </svg>
                                                    </button>
                                                </>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {showCreateEditModal && (
                        <div className="fixed inset-0 flex justify-center items-center z-50" id="marketingmodalopen" style={{ width: "100vw", height: "100vh" }}>
                            <div className="absolute inset-0 bg-gray-800 bg-opacity-60 backdrop-blur-sm" style={{ width: "100vw", height: "150%", background: "black", backgroundSize: "200% 200vh", overflowY: "hidden", paddingBottom: "150px" }}></div>
                            <div className="relative bg-white rounded-lg shadow-lg" style={{ height: "98vh", width: "98vw", overflowY: "auto" }}>
                                <div className="overflow-auto max-h-[90vh]" style={{ padding: "50px 10px 100px 100px", paddingTop: "20px", borderRadius: "10px" }}>
                                    <div className="modal-header text-white bg-blue-500 rounded">
                                        <h5 className="modal-title px-3 py-2 text-white bg-blue-500">{editingSop ? "Update SOP" : "Create New SOP"}</h5>
                                        <button type="button" className="btn-close btn-close-white text-white" onClick={closeCreateEditModal}></button>
                                    </div>
                                    <form onSubmit={handleSubmit}>
                                        <div className="mb-3">
                                            <label className="form-label fw-bold">SOP Title</label>
                                            <input type="text" name="name" value={editingSop ? editingSop.name || "" : newSop.name || ""} onChange={(e) => editingSop ? setEditingSop({ ...editingSop, name: e.target.value }) : setNewSop({ ...newSop, name: e.target.value })} className="form-control" placeholder="Enter SOP Title (e.g. Finance SOP)" />
                                        </div>

                                        <div className="mb-3">
                                            <label className="form-label fw-bold">Designation (Optional)</label>
                                            <select name="designation" value={editingSop ? editingSop.designation || "" : newSop.designation || ""} onChange={(e) => editingSop ? setEditingSop({ ...editingSop, designation: e.target.value }) : setNewSop({ ...newSop, designation: e.target.value })} className="form-select">
                                                <option value="">Select Designation</option>
                                                {designations.map((designation) => (
                                                    <option key={designation.id} value={designation.id}>{designation.name}</option>
                                                ))}
                                            </select>
                                        </div>

                                        <div className="mb-3">
                                            <label className="form-label fw-bold">Assign to Users (Optional)</label>
                                            <div className="border rounded p-2" style={{ maxHeight: "150px", overflowY: "auto" }}>
                                                {users.map((user) => (
                                                    <div key={user.id} className="form-check">
                                                        <input className="form-check-input" type="checkbox" id={`user-${user.id}`} value={user.id} checked={editingSop ? (editingSop.users || []).includes(user.id) : (newSop.users || []).includes(user.id)} onChange={(e) => {
                                                            const userId = parseInt(e.target.value);
                                                            const isChecked = e.target.checked;
                                                            if (editingSop) {
                                                                const currentUsers = editingSop.users || [];
                                                                if (isChecked) setEditingSop({ ...editingSop, users: [...currentUsers, userId] });
                                                                else setEditingSop({ ...editingSop, users: currentUsers.filter(id => id !== userId) });
                                                            } else {
                                                                const currentUsers = newSop.users || [];
                                                                if (isChecked) setNewSop({ ...newSop, users: [...currentUsers, userId] });
                                                                else setNewSop({ ...newSop, users: currentUsers.filter(id => id !== userId) });
                                                            }
                                                        }} />
                                                        <label className="form-check-label" htmlFor={`user-${user.id}`}>{user.name} ({user.username})</label>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>

                                        <div className="mb-3">
                                            <label className="form-label fw-bold">Existing Files</label>
                                            <div className="border rounded p-3 bg-light">
                                                {editingSop && editingSop.files && editingSop.files.length > 0 ? (
                                                    <ul className="list-group">
                                                        {editingSop.files.map((file) => (
                                                            <li key={file.id} className="list-group-item d-flex justify-content-between align-items-center">
                                                                <div>
                                                                    <span className={`badge bg-${file.file_type === 'pdf' ? 'danger' : file.file_type === 'ppt' ? 'warning' : 'primary'} me-2`}>
                                                                        {file.file_type.toUpperCase()}
                                                                    </span>
                                                                    <a href={`${API_BASE_URL}${file.file_url}`} target="_blank" rel="noopener noreferrer">
                                                                        {file.file_name}
                                                                    </a>
                                                                </div>
                                                                <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => deleteSopFile(file.id)}>
                                                                    Delete
                                                                </button>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                ) : (
                                                    <p className="text-muted mb-0">No files uploaded yet.</p>
                                                )}
                                            </div>
                                        </div>

                                        <div className="row">
                                            <div className="col-md-4 mb-3">
                                                <label className="form-label fw-bold">Upload PPT Files</label>
                                                <input type="file" multiple accept=".ppt,.pptx" onChange={(e) => handleFileChange(e, 'ppt')} className="form-control" />
                                            </div>
                                            <div className="col-md-4 mb-3">
                                                <label className="form-label fw-bold">Upload PDF Files</label>
                                                <input type="file" multiple accept=".pdf" onChange={(e) => handleFileChange(e, 'pdf')} className="form-control" />
                                            </div>
                                            <div className="col-md-4 mb-3">
                                                <label className="form-label fw-bold">Upload DOC Files</label>
                                                <input type="file" multiple accept=".doc,.docx" onChange={(e) => handleFileChange(e, 'doc')} className="form-control" />
                                            </div>
                                        </div>

                                        <div className="mb-3">
                                            <label className="form-label fw-bold">Description</label>
                                            <CKEditor 
                                                editor={ClassicEditor} 
                                                data={editingSop?.desc || newSop.desc || ""} 
                                                onChange={(event, editor) => {
                                                    const data = editor.getData();
                                                    if (editingSop) setEditingSop({ ...editingSop, desc: data });
                                                    else setNewSop({ ...newSop, desc: data });
                                                }}
                                                config={{
                                                    toolbar: [
                                                        "heading", "|", "bold", "italic", "underline", "strikethrough", "link", "subscript", "superscript", "highlight", "|",
                                                        "bulletedList", "numberedList", "todoList", "|", "alignment", "indent", "outdent", "|", "blockQuote", "code", "codeBlock", "horizontalLine", "|",
                                                        "insertTable", "tableColumn", "tableRow", "mergeTableCells", "|", "imageUpload", "mediaEmbed", "removeFormat", "undo", "redo", "|",
                                                        "fontSize", "fontFamily", "fontColor", "fontBackgroundColor", "highlight",
                                                    ],
                                                    table: { contentToolbar: ["tableColumn", "tableRow", "mergeTableCells", "tableCellProperties", "tableProperties"] },
                                                    mediaEmbed: { previewsInData: true }
                                                }}
                                            />
                                        </div>

                                        <div className="text-end mt-4">
                                            <button type="button" className="btn btn-secondary me-2" onClick={closeCreateEditModal}>Cancel</button>
                                            <button type="submit" className="btn btn-primary">Save</button>
                                        </div>
                                    </form>
                                </div>
                            </div>
                        </div>
                    )}

                    {showViewDescModal && (
                        <div className="fixed inset-0 flex justify-center items-center z-50" style={{ width: "100vw", height: "100vh" }}>
                            <div className="absolute inset-0 bg-gray-800 bg-opacity-60 backdrop-blur-sm" style={{ width: "100vw", height: "150%", background: "black", backgroundSize: "200% 200vh", paddingBottom: "150px" }}></div>
                            <div className="relative bg-white rounded-lg shadow-lg" style={{ height: "98%", overflowY: "auto", margin: "10px" }}>
                                <div className="overflow-auto max-h-[80vh]" style={{ padding: "10px 10px 100px 100px", paddingTop: "20px", border: "1px solid gray" }}>
                                    <h2 className="text-center text-blue-700 mb-2">Description</h2>
                                    <button type="button" onClick={closeViewDescModal} className="bg-blue-500 text-white px-3 py-2 rounded hover:bg-red-700 transition-colors" style={{ position: "absolute", top: "20px", right: "20px" }}>X</button>
                                    <div className="modal-body border p-2"><div dangerouslySetInnerHTML={{ __html: currentDesc }}></div></div>
                                    <div className="modal-footer mt-3"><button type="button" className="btn btn-secondary" onClick={closeViewDescModal}>Close</button></div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

export default SopManagementPage;
